import CircledButton from 'common/components/buttons/CircledButton';
import SvgRender from 'common/components/general/SvgRender';
import binIcon from 'common/assets/svg/actions/delete.svg';
import AddMore from 'common/components/form/helpers/AddMore';

import { CSSProperties, FC, ReactNode } from 'react';
import { FieldsStateType, FormState } from '@/common/types/form';

type FormListProps<T extends Record<string, any> = Record<string, any>> = {
  inner?: boolean;
  subFormInline?: boolean;
  showBin?: boolean;
  sideBin?: boolean;
  forms: FormState<T>[];
  addText?: string;
  children: (form: FormState<T>, index: number) => ReactNode;
  onAdd?: () => void;
  onRemove?: (index: number, form: FormState<T>) => void;
  filter?: (state: FieldsStateType<T>) => boolean;
  canRemove?: (form: FormState<T>, index: number) => boolean;
  allowRemoveLast?: boolean;
  canAdd?: boolean;
  className?: string;
  formListclassName?: string;
  style?: CSSProperties;
  footer?: ReactNode;
  removeButtonProps?: Record<string, any>;
};

const FormList = <T extends Record<string, any> = Record<string, any>>({
  inner,
  subFormInline = false,
  showBin = true,
  sideBin = false,
  forms,
  addText,
  children,
  onAdd,
  onRemove,
  filter = undefined,
  canRemove,
  allowRemoveLast = false,
  canAdd = true,
  className,
  formListclassName,
  style,
  footer,
  removeButtonProps
}: FormListProps<T>) => {
  const filteredForms = forms.filter((formState: FormState<T>) =>
    filter ? filter(formState.state) : true
  );

  return (
    <div
      className={`form-list-wrap ${subFormInline ? 'sub-form-inline' : ''} ${
        className?.includes('pt-') ? className : 'pt-1 pb-1'
      }`}
      style={style}
    >
      {filteredForms.map((form: FormState<T>, index: number) => {
        const hasMultipleForms = filteredForms.length > 1;
        const isRemovable =
          (canRemove ? canRemove(form, index) : true) &&
          onRemove &&
          (allowRemoveLast || hasMultipleForms);

        return (
          <div
            key={form.index}
            className={`form-list${inner ? ' inner' : ''} ${formListclassName || ''}`}
          >
            {isRemovable && (
              <RemoveForm
                onClick={() => onRemove(form.index ?? 0, form)}
                variant={!!inner}
                showBin={showBin}
                sideBin={sideBin}
                removeButtonProps={removeButtonProps}
              />
            )}
            {children(form, index)}
          </div>
        );
      })}
      <div className="form-list-footer d-flex align-items-center justify-content-between">
        {onAdd && canAdd && <AddMore onClick={onAdd} variant={inner} label={addText}></AddMore>}
        {footer ? footer : null}
      </div>
    </div>
  );
};
export default FormList;

type RemoveFormProps = {
  onClick: () => void;
  variant: boolean;
  showBin: boolean;
  sideBin: boolean;
  removeButtonProps?: Record<string, any>;
};

const RemoveForm: FC<RemoveFormProps> = ({
  onClick,
  variant,
  showBin,
  sideBin,
  removeButtonProps = {}
}) =>
  variant ? (
    showBin ? (
      sideBin ? (
        <SvgRender
          onClick={onClick}
          className="cursor-pointer text-red position-absolute"
          src={binIcon}
          style={{ width: 16, height: 16, top: 4, right: -24 }}
        />
      ) : (
        <SvgRender
          style={{ width: 14, height: 14 }}
          onClick={onClick}
          src={binIcon}
          className="remove-icon variant"
        />
      )
    ) : (
      <CircledButton
        onClick={onClick}
        type="remove"
        className="remove-icon"
        style={{ width: 16, height: 16 }}
        {...removeButtonProps}
      />
    )
  ) : (
    <CircledButton
      onClick={onClick}
      type="remove"
      className="remove-icon"
      style={{ width: 16, height: 16 }}
      {...removeButtonProps}
    />
  );
